import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ColumnDef, ColumnFiltersState } from '@tanstack/react-table';
import { Search, Plus, Edit2, Ban, CheckCircle, Trash2, Key, X, Users, AlertCircle, PlugZap, Clock, UserX, UserCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DataTable } from '../components/common/DataTable';
import {
  UserProfile,
  CreateUserInput,
  UpdateUserInput,
  listUsers,
  createUser,
  updateUser,
  suspendUser,
  activateUser,
  deleteUser,
  resetUserPassword,
  disableUserMcpAdmin,
  reinstateUser
} from '../services/userManagement';
import { NORMAL_ROLE_ID, Role, apiErrorMessage, listRoles, setUserRoles } from '../services/roles';
import { RolePicker } from '../components/admin/RolePicker';
import OffboardUserModal from '../components/admin/OffboardUserModal';

type SignInMethod = 'password' | 'microsoft' | 'both';

const SIGN_IN_LABELS: Record<SignInMethod, string> = { password: 'Password', microsoft: 'Microsoft', both: 'Both' };

function getSignInMethod(user: UserProfile): SignInMethod {
  if (user.provider === 'entra') return 'microsoft';
  return user.entraLinked ? 'both' : 'password';
}

function getSignInBadgeClass(method: SignInMethod): string {
  switch (method) {
    case 'microsoft': return 'bg-sky-100 text-sky-700 border border-sky-200';
    case 'both': return 'bg-primary/10 text-primary border border-primary/20';
    default: return 'bg-slate-100 text-slate-500 border border-slate-200';
  }
}

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'active': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    case 'pending': return 'bg-sky-100 text-sky-700 border border-sky-200';
    case 'suspended': return 'bg-amber-100 text-amber-700 border border-amber-200';
    case 'deleted': return 'bg-red-100 text-red-600 border border-red-200';
    default: return 'bg-slate-100 text-slate-500 border border-slate-200';
  }
}

type FilterKey = 'roles' | 'status' | 'organizationId' | 'calendarMcp' | 'signInMethod';

const EMPTY_FILTERS: Record<FilterKey, string> = { roles: '', status: '', organizationId: '', calendarMcp: '', signInMethod: '' };

const roleIdsOf = (u: UserProfile) => (u.roleAssignments ?? []).map(a => a.roleId);
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every(x => b.includes(x));

const filterSelectCls = "border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white";

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offboardTarget, setOffboardTarget] = useState<UserProfile | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  const [createForm, setCreateForm] = useState<CreateUserInput>({
    email: '',
    firstName: '',
    lastName: '',
    organizationId: currentUser?.organizationId || '',
    roleIds: [NORMAL_ROLE_ID],
    temporaryPassword: ''
  });

  const [editForm, setEditForm] = useState<UpdateUserInput>({});
  const [editRoleIds, setEditRoleIds] = useState<string[]>([]);

  // Only the first load shows the spinner, so a refetch after a row action keeps the table in place.
  const fetchUsers = useCallback(async () => {
    try {
      setError(null);
      const result = await listUsers();
      setUsers(result.users);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    listRoles().then(setRoles).catch((err) => setRolesError(apiErrorMessage(err, 'Failed to load roles')));
  }, []);

  const roleName = useMemo(() => new Map(roles.map(r => [r.id, r.name])), [roles]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput.trim()), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleCreateUser = async () => {
    try {
      const result = await createUser(createForm);
      alert(`User created successfully!\nTemporary password: ${result.temporaryPassword}`);
      setShowCreateModal(false);
      setCreateForm({ email: '', firstName: '', lastName: '', organizationId: currentUser?.organizationId || '', roleIds: [NORMAL_ROLE_ID], temporaryPassword: '' });
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to create user: ${apiErrorMessage(err, 'unknown error')}`);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      await updateUser(selectedUser.userId, editForm);
      const before = roleIdsOf(selectedUser);
      if (!sameSet(before, editRoleIds)) await setUserRoles(selectedUser.userId, editRoleIds, before);
      alert('User updated successfully!');
      setShowEditModal(false);
      setSelectedUser(null);
      setEditForm({});
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to update user: ${apiErrorMessage(err, 'unknown error')}`);
      fetchUsers();
    }
  };

  const handleSuspendUser = async (userId: string) => {
    const reason = window.prompt('Reason for suspension (optional):');
    if (reason === null) return;
    try {
      const result = await suspendUser(userId, reason || undefined);
      alert(result.partial ? result.message : 'User suspended successfully!');
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to suspend user: ${err.message}`);
    }
  };

  const handleActivateUser = async (userId: string) => {
    if (!window.confirm('Activate this user?')) return;
    try {
      await activateUser(userId);
      alert('User activated successfully!');
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to activate user: ${err.message}`);
    }
  };

  const handleReinstateUser = async (user: UserProfile) => {
    if (!window.confirm(`Reinstate ${user.email}? Their account will be reactivated. They'll need to create new project MCP tokens themselves.`)) return;
    try {
      await reinstateUser(user.userId);
      alert('User reinstated successfully!');
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to reinstate user: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await deleteUser(userId);
      alert('User deleted successfully!');
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to delete user: ${err.message}`);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!window.confirm('Reset password for this user?')) return;
    try {
      const result = await resetUserPassword(userId);
      alert(`Password reset successfully! Temporary password: ${result.temporaryPassword}`);
    } catch (err: any) {
      alert(`Failed to reset password: ${err.message}`);
    }
  };

  const handleDisableMcp = async (userId: string) => {
    if (!window.confirm("Disable this user's calendar MCP access? Their token stays stored but stops working — they'd need to re-enable it themselves to get a new one.")) return;
    try {
      await disableUserMcpAdmin(userId);
      fetchUsers();
    } catch (err: any) {
      alert(`Failed to disable MCP access: ${err.message}`);
    }
  };

  const openEditModal = (user: UserProfile) => {
    setSelectedUser(user);
    setEditForm({ firstName: user.firstName, lastName: user.lastName, organizationId: user.organizationId });
    setEditRoleIds(roleIdsOf(user));
    setShowEditModal(true);
  };

  const setFilter = (key: FilterKey, value: string) => setFilters(f => ({ ...f, [key]: value }));

  const columnFilters = useMemo<ColumnFiltersState>(
    () => Object.entries(filters).filter(([, value]) => value).map(([id, value]) => ({ id, value })),
    [filters]
  );
  const hasFilters = searchInput !== '' || columnFilters.length > 0;

  const orgOptions = useMemo(() => Array.from(new Set(users.map(u => u.organizationId))).sort(), [users]);
  const pendingCount = useMemo(() => users.filter(u => u.status === 'pending').length, [users]);

  const columns: ColumnDef<UserProfile, any>[] = [
    {
      id: 'user',
      header: 'User',
      accessorFn: u => u.displayName,
      meta: { className: 'max-w-[200px]' },
      cell: ({ row }) => (
        <>
          <p className="font-medium text-navy truncate" title={row.original.displayName}>{row.original.displayName}</p>
          <p className="text-xs text-slate-400 truncate mt-0.5" title={row.original.email}>{row.original.email}</p>
        </>
      ),
    },
    {
      accessorKey: 'organizationId',
      header: 'Organisation',
      filterFn: 'equalsString',
      meta: { className: 'text-slate-500' },
    },
    {
      id: 'roles',
      header: 'Roles',
      accessorFn: roleIdsOf,
      enableSorting: false,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => (
        <div className="flex gap-1.5 flex-wrap">
          {(getValue() as string[]).map(id => (
            <span key={id} className="inline-block px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-semibold">
              {roleName.get(id) ?? 'Unknown role'}
            </span>
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      filterFn: 'equalsString',
      cell: ({ row, getValue }) => (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusBadgeClass(getValue())}`}>
          {row.original.isOffboarded ? 'Offboarded' : getValue()}
        </span>
      ),
    },
    {
      id: 'signInMethod',
      header: 'Sign-in',
      accessorFn: getSignInMethod,
      filterFn: 'equalsString',
      cell: ({ getValue }) => (
        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${getSignInBadgeClass(getValue())}`}>
          {SIGN_IN_LABELS[getValue() as SignInMethod]}
        </span>
      ),
    },
    {
      id: 'calendarMcp',
      header: 'Calendar MCP',
      accessorFn: u => (u.mcpEnabled ? 'on' : 'off'),
      enableSorting: false,
      filterFn: 'equalsString',
      cell: ({ row }) => row.original.mcpEnabled ? (
        <div className="flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Enabled{row.original.mcpWriteEnabled ? ' · write' : ''}
          </span>
          {row.original.mcpTokenHint && (
            <span className="text-[11px] text-slate-400">…{row.original.mcpTokenHint}</span>
          )}
        </div>
      ) : (
        <span className="text-xs text-slate-400">—</span>
      ),
    },
    {
      id: 'lastLoginAt',
      header: 'Last Login',
      // ISO timestamps sort as strings; never-logged-in users ('') land last when newest-first.
      accessorFn: u => u.lastLoginAt ?? '',
      sortingFn: 'basic',
      sortDescFirst: true,
      meta: { className: 'text-slate-400 text-xs' },
      cell: ({ getValue }) => (getValue() ? new Date(getValue()).toLocaleDateString() : 'Never'),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      meta: { align: 'right' },
      cell: ({ row }) => {
        const user = row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <button onClick={() => openEditModal(user)} title="Edit"
              className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors">
              <Edit2 size={13} />
            </button>
            <button onClick={() => handleResetPassword(user.userId)} title="Reset Password"
              className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors">
              <Key size={13} />
            </button>
            {user.isOffboarded ? (
              <button onClick={() => handleReinstateUser(user)} title="Reinstate"
                className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors">
                <UserCheck size={13} />
              </button>
            ) : user.status === 'active' ? (
              <button onClick={() => handleSuspendUser(user.userId)} title="Suspend"
                className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors">
                <Ban size={13} />
              </button>
            ) : (
              <button onClick={() => handleActivateUser(user.userId)} title="Activate"
                className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors">
                <CheckCircle size={13} />
              </button>
            )}
            {user.mcpEnabled && (
              <button onClick={() => handleDisableMcp(user.userId)} title="Disable calendar MCP access"
                className="p-1.5 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100 transition-colors">
                <PlugZap size={13} />
              </button>
            )}
            {!user.isOffboarded && user.userId !== currentUser?.userId && (
              <button onClick={() => setOffboardTarget(user)} title="Offboard (leaver)"
                className="p-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                <UserX size={13} />
              </button>
            )}
            {user.userId !== currentUser?.userId && (
              <button onClick={() => handleDeleteUser(user.userId)} title="Delete"
                className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors">
                <Trash2 size={13} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const inputCls = "w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none";

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Users size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">User Management</h1>
              <p className="text-slate-500 text-sm font-medium">Manage user accounts, roles, and permissions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <button
                onClick={() => setFilter('status', filters.status === 'pending' ? '' : 'pending')}
                className={`flex items-center justify-center gap-2 rounded-xl h-10 px-4 border text-sm font-bold shadow-sm transition-colors ${
                  filters.status === 'pending' ? 'bg-sky-600 border-sky-600 text-white' : 'bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100'
                }`}
              >
                <Clock size={16} /> {pendingCount} pending approval
              </button>
            )}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm"
            >
              <Plus size={16} /> Create User
            </button>
          </div>
        </header>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <AlertCircle size={16} className="shrink-0" /> {error}
          </div>
        )}

        <DataTable
          data={users}
          columns={columns}
          getRowId={u => u.userId}
          loading={loading}
          globalFilter={searchTerm}
          getSearchText={u => `${u.displayName} ${u.firstName} ${u.lastName} ${u.email} ${u.organizationId}`}
          columnFilters={columnFilters}
          initialSorting={[{ id: 'lastLoginAt', desc: true }]}
          emptyState={
            <div className="text-center py-16 text-slate-400">
              <Users size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No users found</p>
            </div>
          }
          toolbar={
            <>
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search name, email or organisation..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
              <select value={filters.roles} onChange={(e) => setFilter('roles', e.target.value)} className={filterSelectCls}>
                <option value="">All Roles</option>
                {roles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
              </select>
              <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)} className={filterSelectCls}>
                <option value="">All Status</option>
                <option value="pending">Pending Approval</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
              <select value={filters.organizationId} onChange={(e) => setFilter('organizationId', e.target.value)} className={filterSelectCls}>
                <option value="">All Organisations</option>
                {orgOptions.map(org => <option key={org} value={org}>{org}</option>)}
              </select>
              <select value={filters.signInMethod} onChange={(e) => setFilter('signInMethod', e.target.value)} className={filterSelectCls}>
                <option value="">All Sign-in</option>
                {(Object.keys(SIGN_IN_LABELS) as SignInMethod[]).map(m => <option key={m} value={m}>{SIGN_IN_LABELS[m]}</option>)}
              </select>
              <select value={filters.calendarMcp} onChange={(e) => setFilter('calendarMcp', e.target.value)} className={filterSelectCls}>
                <option value="">Calendar MCP: any</option>
                <option value="on">Calendar MCP: on</option>
                <option value="off">Calendar MCP: off</option>
              </select>
              {hasFilters && (
                <button onClick={() => { setSearchInput(''); setFilters(EMPTY_FILTERS); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-500 hover:bg-slate-100 transition-colors">
                  <X size={14} /> Clear filters
                </button>
              )}
            </>
          }
        />

      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-navy">Create New User</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Email *</label>
                <input type="email" value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">First Name *</label>
                  <input type="text" value={createForm.firstName}
                    onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                    className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Last Name *</label>
                  <input type="text" value={createForm.lastName}
                    onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                    className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Organisation ID *</label>
                <input type="text" value={createForm.organizationId}
                  onChange={(e) => setCreateForm({ ...createForm, organizationId: e.target.value })}
                  className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Roles *</label>
                {rolesError ? <p className="text-sm text-red-600">{rolesError}</p> : (
                  <RolePicker roles={roles} value={createForm.roleIds} onChange={(roleIds) => setCreateForm({ ...createForm, roleIds })} />
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Temporary Password</label>
                <input type="password" value={createForm.temporaryPassword}
                  onChange={(e) => setCreateForm({ ...createForm, temporaryPassword: e.target.value })}
                  placeholder="Leave blank to auto-generate"
                  className={inputCls} />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleCreateUser}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
                  Create User
                </button>
                <button onClick={() => setShowCreateModal(false)}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-navy">Edit User</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors">
                <X size={18} />
              </button>
            </div>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">First Name</label>
                  <input type="text" value={editForm.firstName || ''}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                    className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Last Name</label>
                  <input type="text" value={editForm.lastName || ''}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                    className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Organisation ID</label>
                <input type="text" value={editForm.organizationId || ''}
                  onChange={(e) => setEditForm({ ...editForm, organizationId: e.target.value })}
                  className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Roles</label>
                {rolesError ? <p className="text-sm text-red-600">{rolesError}</p> : (
                  <RolePicker roles={roles} value={editRoleIds} onChange={setEditRoleIds} />
                )}
                <p className="mt-2 text-xs text-slate-400">Integrations, modules and features are roles too. Manage what each role grants on the Roles page.</p>
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleUpdateUser}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
                  Update User
                </button>
                <button onClick={() => setShowEditModal(false)}
                  className="flex-1 flex items-center justify-center gap-2 rounded-xl h-10 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {offboardTarget && (
        <OffboardUserModal
          userId={offboardTarget.userId}
          displayName={[offboardTarget.firstName, offboardTarget.lastName].filter(Boolean).join(' ') || offboardTarget.email}
          onClose={() => setOffboardTarget(null)}
          onOffboarded={() => fetchUsers()}
        />
      )}
    </div>
  );
}
